import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRevenueReport, useUtilizationReport } from '@/api/hooks/useAdminReports';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { useFileDownload } from '@/hooks/useFileDownload';
import { formatCurrency } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  MapPin,
  DollarSign,
  CalendarDays,
  Clock,
  BarChart3,
  Download,
  Loader2,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts';
import { cn } from '@/lib/utils';

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function endOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
}
function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}
function mondayOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}
function sundayOfWeek() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() + (day === 0 ? 0 : 7 - day);
  return new Date(d.getFullYear(), d.getMonth(), diff).toISOString().split('T')[0];
}

// Keyed by sports.code (stable, locale-independent). Survives locale switches
// and additional sports — see DECISIONS.md "Operator-Admin Hardcoded SPORTS
// Arrays → Tenant-Aware Hook" (June 8 2026) for the previous display-name-keyed
// version's fragility. Pick from the existing brand palette for the three
// original sports; the four newer sports get distinct hues from the same
// HSL space — refine later if any look off in the chart.
const SPORT_COLORS: Record<string, string> = {
  GOLF_SIM: 'hsl(30, 60%, 50%)',
  CRICKET: 'hsl(140, 50%, 45%)',
  PADEL: 'hsl(220, 60%, 55%)',
  SIM_RACING: 'hsl(0, 65%, 55%)',
  BASEBALL: 'hsl(280, 50%, 55%)',
  BADMINTON: 'hsl(180, 55%, 45%)',
  BOWLING: 'hsl(50, 70%, 50%)',
};
const FALLBACK_SPORT_COLOR = 'hsl(30, 60%, 50%)';

type Preset = { key: string; start: () => string; end: () => string };

const PRESETS: Preset[] = [
  { key: 'today', start: today, end: today },
  { key: 'this_week', start: mondayOfWeek, end: sundayOfWeek },
  { key: 'this_month', start: firstOfMonth, end: endOfMonth },
  { key: 'last_30', start: () => daysAgo(30), end: today },
];

export default function ReportsPage() {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(endOfMonth);
  const [groupBy, setGroupBy] = useState('DAY');
  const [locationFilter, setLocationFilter] = useState('all');
  const [activePreset, setActivePreset] = useState('this_month');

  const { downloadFile, isDownloading } = useFileDownload();
  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];
  const locationId = locationFilter !== 'all' ? locationFilter : undefined;

  const { data: revenue, isLoading: revenueLoading } = useRevenueReport(startDate, endDate, groupBy, locationId);
  const { data: utilization, isLoading: utilizationLoading } = useUtilizationReport(startDate, endDate, locationId);

  const locationLabel =
    locationFilter === 'all'
      ? t('admin.all_locations')
      : locations.find((l) => l.locationId === locationFilter)?.locationName ?? t('admin.all_locations');

  const totalRevenue = revenue?.periods.reduce((sum, p) => sum + p.netRevenue, 0) ?? 0;
  const totalBookings = revenue?.periods.reduce((sum, p) => sum + p.bookingCount, 0) ?? 0;

  const totalHoursBooked = utilization?.units.reduce((sum, u) => sum + u.totalHoursBooked, 0) ?? 0;
  const totalHoursAvailable = utilization?.units.reduce((sum, u) => sum + u.totalHoursAvailable, 0) ?? 0;
  const avgUtilization = totalHoursAvailable > 0 ? (totalHoursBooked / totalHoursAvailable) * 100 : 0;

  const setPreset = (key: string, start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    setActivePreset(key);
  };

  const exportUrl = (type: string, format: string) => {
    const base = `/api/v1/admin/reports/${type}/export`;
    const params = new URLSearchParams({ startDate, endDate, format });
    if (type === 'revenue') params.set('groupBy', groupBy);
    if (locationId) params.set('locationId', locationId);
    return `${base}?${params.toString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('admin.reports')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={isDownloading} onClick={() => downloadFile(exportUrl('revenue', 'csv'), `revenue_${startDate}_${endDate}.csv`)}>
            {isDownloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} {t('admin.export_csv')}
          </Button>
          <Button variant="outline" size="sm" disabled={isDownloading} onClick={() => downloadFile(exportUrl('revenue', 'pdf'), `revenue_${startDate}_${endDate}.pdf`)}>
            {isDownloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} {t('admin.export_pdf')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <div className="flex items-center gap-1.5">
            <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setActivePreset(''); }} className="w-36" />
            <span className="text-muted-foreground text-sm">—</span>
            <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setActivePreset(''); }} className="w-36" />
          </div>

          {/* Pill presets */}
          <div className="flex gap-1">
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                variant={activePreset === p.key ? 'default' : 'outline'}
                size="sm"
                className="whitespace-nowrap rounded-full"
                onClick={() => setPreset(p.key, p.start(), p.end())}
              >
                {t(`admin.preset_${p.key}`)}
              </Button>
            ))}
          </div>

          {/* Group by segmented control */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">{t('admin.group_by')}:</span>
            <Tabs value={groupBy} onValueChange={(v) => setGroupBy(v ?? 'DAY')}>
              <TabsList className="h-8">
                <TabsTrigger value="DAY" className="text-xs px-3">{t('admin.group_daily')}</TabsTrigger>
                <TabsTrigger value="WEEK" className="text-xs px-3">{t('admin.group_weekly')}</TabsTrigger>
                <TabsTrigger value="MONTH" className="text-xs px-3">{t('admin.group_monthly')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {locations.length > 1 && (
            <Select value={locationFilter} onValueChange={(v) => setLocationFilter(v ?? 'all')}>
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">{locationLabel}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_locations')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground truncate">{t('admin.total_revenue')}</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(totalRevenue, 'CAD', i18n.language)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground truncate">{t('admin.total_hours_booked')}</p>
              <p className="text-xl font-bold font-mono">{totalHoursBooked.toFixed(1)}h</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground truncate">{t('admin.avg_utilization')}</p>
              <p className="text-xl font-bold font-mono">{avgUtilization.toFixed(1)}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground truncate">{t('admin.total_sessions')}</p>
              <p className="text-xl font-bold">{totalBookings}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="revenue">
        <TabsList>
          <TabsTrigger value="revenue">{t('admin.tab_revenue')}</TabsTrigger>
          <TabsTrigger value="utilization">{t('admin.tab_utilization')}</TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          {revenueLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-72" />
              <Skeleton className="h-40" />
            </div>
          ) : revenue && revenue.periods.length > 0 ? (
            <div className="space-y-6">
              {/* Area chart */}
              <Card>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={revenue.periods}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(30, 60%, 50%)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(30, 60%, 50%)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip formatter={(value) => formatCurrency(Number(value), 'CAD', i18n.language)} />
                      <Area type="monotone" dataKey="netRevenue" stroke="hsl(30, 60%, 50%)" fill="url(#revenueGradient)" strokeWidth={2} name={t('admin.net_revenue')} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Revenue summary table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[100px]">{t('admin.label_date')}</TableHead>
                          <TableHead className="min-w-[80px]">{t('admin.total_bookings')}</TableHead>
                          <TableHead className="min-w-[100px]">{t('admin.col_revenue')}</TableHead>
                          <TableHead className="min-w-[100px]">{t('admin.avg_per_booking')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {revenue.periods.map((p, i) => (
                          <TableRow key={p.period} className={cn(i % 2 === 0 && 'bg-muted/30')}>
                            <TableCell className="text-sm">{p.period}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{p.bookingCount}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{formatCurrency(p.netRevenue, 'CAD', i18n.language)}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{formatCurrency(p.averageBookingValue, 'CAD', i18n.language)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t('admin.no_revenue_data')}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Utilization Tab */}
        <TabsContent value="utilization">
          {utilizationLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-64" />
              <Skeleton className="h-40" />
            </div>
          ) : utilization && utilization.units.length > 0 ? (
            <div className="space-y-6">
              {/* Horizontal bar chart */}
              <Card>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={Math.max(200, utilization.units.length * 40)}>
                    <BarChart data={utilization.units} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                      <YAxis type="category" dataKey="unitName" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                      <Bar dataKey="utilizationPercent" radius={[0, 4, 4, 0]} name={t('admin.utilization')}>
                        {utilization.units.map((u) => (
                          <Cell key={u.unitId} fill={SPORT_COLORS[u.sportCode] ?? FALLBACK_SPORT_COLOR} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Utilization table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[120px]">{t('admin.col_unit')}</TableHead>
                          <TableHead className="hidden md:table-cell">{t('admin.col_sport')}</TableHead>
                          <TableHead className="min-w-[100px] text-right">{t('admin.hours_booked')}</TableHead>
                          <TableHead className="hidden sm:table-cell text-right">{t('admin.hours_available')}</TableHead>
                          <TableHead className="min-w-[160px]">{t('admin.utilization')}</TableHead>
                          <TableHead className="min-w-[80px] text-right">{t('admin.col_revenue')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {utilization.units.map((u, i) => (
                          <TableRow key={u.unitId} className={cn(i % 2 === 0 && 'bg-muted/30')}>
                            <TableCell className="font-medium text-sm">{u.unitName}</TableCell>
                            <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{t(`sport_${u.sportCode}`, { defaultValue: u.sport })}</TableCell>
                            <TableCell className="font-mono text-sm text-right">{u.totalHoursBooked.toFixed(1)}h</TableCell>
                            <TableCell className="hidden sm:table-cell font-mono text-sm text-muted-foreground text-right">{u.totalHoursAvailable.toFixed(1)}h</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="h-2.5 flex-1 max-w-[100px] rounded-full bg-muted overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${Math.min(u.utilizationPercent, 100)}%`,
                                      backgroundColor: SPORT_COLORS[u.sportCode] ?? FALLBACK_SPORT_COLOR,
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-mono min-w-[40px]">{u.utilizationPercent.toFixed(1)}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-right">{formatCurrency(u.revenue, 'CAD', i18n.language)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t('admin.no_utilization_data')}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
